import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { fetchJoinRequests } from '../../services/joinRequestService';
import { createLogger } from '../../utils/logger';

const logger = createLogger('useCanViewJoinRequests');

// Resolved outcomes, keyed by `${userId}:${orgId}`. Every route mounts its own
// <Layout>, so the sidebar (and this hook) remounts on each navigation — without a
// cache the probe re-ran and the link flickered on every page change.
const resolved = new Map<string, boolean>();

/**
 * The Join Requests page itself got a 403 — the user's approval rights were revoked
 * after their user record (and its `can_view_join_requests`) was cached. Recording it
 * here hides the link on the next sidebar mount instead of after the 30-min auth cache.
 */
export function recordJoinRequestsAccessDenied(userId?: string | null, orgId?: string | null): void {
  if (userId && orgId) resolved.set(`${userId}:${orgId}`, false);
}

/** Test-only: forget cached outcomes between cases. */
export function resetCanViewJoinRequestsCache(): void {
  resolved.clear();
}

/**
 * Whether the signed-in user should see the "Join Requests" nav link at all.
 *
 * Admin and super admin always do — only a Trainer's access depends on approval
 * rights (Billing Owner or the per-account switch), and the server 403s
 * `list_join_requests` for a Trainer without them (see that endpoint). Rather than
 * duplicate that rule here, this just probes the same endpoint the page itself
 * loads and reads the outcome — so the sidebar link and the page it points to can
 * never disagree.
 *
 * The answer normally arrives with the user record itself (`can_view_join_requests`
 * on GET /user/{own id}), so nothing is probed. The probe below is only the fallback
 * for a user record that predates that field (e.g. the 30-min auth cache).
 *
 * For a Trainer the link stays hidden until the probe resolves (showing it first
 * made it flash and then vanish for Trainers without rights). Any non-403 failure
 * still shows it — a network blip must not hide navigation — and only an explicit
 * 403 hides it. The outcome is cached for the page's lifetime.
 *
 * A live outcome — the probe, or the page's own 403 (`recordJoinRequestsAccessDenied`)
 * — wins over the user record, so revoked rights hide the link on the next navigation.
 * Newly granted rights show after a reload or the auth cache's next refresh: nothing
 * on screen can observe them sooner without re-probing the slow list endpoint.
 */
export function useCanViewJoinRequests(): boolean {
  const { currentUser, userRole } = useAuth();
  const orgId = currentUser?.organization_id;
  const fromUser = currentUser?.can_view_join_requests;
  const needsProbe = userRole === 'trainer' && Boolean(orgId) && typeof fromUser !== 'boolean';
  const cacheKey = `${currentUser?.id ?? ''}:${orgId ?? ''}`;
  const [probed, setProbed] = useState<{ key: string; canView: boolean } | null>(null);

  useEffect(() => {
    if (!needsProbe || resolved.has(cacheKey)) return;
    let cancelled = false;
    const settle = (canView: boolean) => {
      resolved.set(cacheKey, canView);
      if (!cancelled) setProbed({ key: cacheKey, canView });
    };
    fetchJoinRequests(orgId as string)
      .then(() => settle(true))
      .catch(err => {
        if ((err as { status?: number }).status === 403) {
          settle(false);
        } else {
          logger.warn('Could not determine join-request access; showing the link', err);
          // Not cached, so the next mount retries.
          if (!cancelled) setProbed({ key: cacheKey, canView: true });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [needsProbe, cacheKey, orgId]);

  if (userRole !== 'trainer' || !orgId) return true;
  const cached = resolved.get(cacheKey);
  if (cached !== undefined) return cached;
  if (typeof fromUser === 'boolean') return fromUser;
  return probed?.key === cacheKey ? probed.canView : false;
}
